import { useState, type MouseEvent } from "react";
import {
  OPPORTUNITY_STATUS_LABELS,
  type OpportunityStatus,
} from "@/types/opportunity";
import type { ProductTypeEntry } from "@/hooks/useOrgSettings";
import { formatOpportunityProductType } from "@/lib/opportunity/productTypeLabel";
import { formatOpportunityCurrency } from "@/lib/opportunity/formatCurrency";
import { archiveOpportunity, deleteOpportunity } from "@/lib/opportunity/opportunityClient";
import type { OpportunityListItem } from "@/lib/opportunity/opportunityListTypes";
import {
  OPPORTUNITY_LIST_PAGE_SIZES,
  type OpportunityListPageSize,
  type OpportunityListSortDir,
  type OpportunityListSortKey,
} from "@/lib/opportunity/opportunityListTypes";
import { useOpportunityListTable } from "@/components/opportunity/useOpportunityListTable";

const STATUS_BADGE: Record<OpportunityStatus, string> = {
  new: "bg-slate-100 text-slate-800 dark:bg-zinc-800 dark:text-zinc-200",
  quoting: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
  revision_requested: "bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-200",
  pending_signature: "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200",
  signed: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200",
  ordered: "bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-200",
  in_process: "bg-teal-100 text-teal-800 dark:bg-teal-950/40 dark:text-teal-200",
  complete: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200",
  lost: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-200",
};

export type OpportunityListTableProps = {
  opportunities: OpportunityListItem[];
  onOpenOpportunity: (id: string) => void;
  productTypes: ReadonlyArray<ProductTypeEntry>;
  onMutated: () => void;
  canDeleteOpportunity: (opp: OpportunityListItem) => boolean;
};

export function OpportunityListTable(props: OpportunityListTableProps) {
  const { opportunities, onOpenOpportunity, productTypes, onMutated, canDeleteOpportunity } =
    props;
  const table = useOpportunityListTable(opportunities);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function handleArchive(e: MouseEvent, id: string) {
    e.stopPropagation();
    if (!window.confirm("Archive this opportunity?")) return;
    setBusyId(id);
    const r = await archiveOpportunity(id);
    setBusyId(null);
    if (r.ok) onMutated();
  }

  async function handleDelete(e: MouseEvent, id: string) {
    e.stopPropagation();
    if (!window.confirm("Permanently delete this opportunity?")) return;
    setBusyId(id);
    const r = await deleteOpportunity(id);
    setBusyId(null);
    if (r.ok) onMutated();
  }

  return (
    <div className="space-y-2">
      <ListControls
        total={opportunities.length}
        rangeStart={table.rangeStart}
        rangeEnd={table.rangeEnd}
        page={table.page}
        pageCount={table.pageCount}
        pageSize={table.pageSize}
        sortKey={table.sortKey}
        sortDir={table.sortDir}
        onSetPage={table.setPage}
        onSetPageSize={table.setPageSize}
        onSetSortKey={table.setSortKey}
        onToggleSortDir={table.toggleSortDir}
        canPrev={table.page > 0}
        canNext={table.page + 1 < table.pageCount}
      />
      <div className="overflow-x-auto overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-zinc-800/60 text-left text-xs uppercase text-gray-600 dark:text-zinc-300">
            <tr>
              <th className="px-3 py-2">Customer / Project</th>
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">Total / Deposit</th>
              <th className="px-3 py-2">Location</th>
              <th className="px-3 py-2">Assignee</th>
              <th className="px-3 py-2">Generated by</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Updated</th>
              <th className="px-3 py-2 w-28">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
            {table.pageItems.map((opp) => (
              <tr
                key={opp.id}
                className="hover:bg-indigo-50/40 dark:hover:bg-indigo-950/20 cursor-pointer"
                onClick={() => onOpenOpportunity(opp.id)}
              >
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900 dark:text-zinc-100">
                    {opp.customer_company || opp.customer_name || opp.project_name || "(no name)"}
                  </div>
                  {opp.project_name && (opp.customer_company || opp.customer_name) ? (
                    <div className="text-xs text-gray-500 dark:text-zinc-400">{opp.project_name}</div>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-xs text-gray-700 dark:text-zinc-300">
                  {formatOpportunityProductType(opp.product_type, productTypes)}
                </td>
                <td className="px-3 py-2 text-xs tabular-nums text-gray-700 dark:text-zinc-300">
                  <PricingCell summary={opp.list_summary} />
                </td>
                <td className="px-3 py-2 text-xs text-gray-600 dark:text-zinc-400">
                  {opp.list_summary?.location_name ?? "—"}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600 dark:text-zinc-400">
                  {opp.list_summary?.assigned_user_name ?? "—"}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600 dark:text-zinc-400">
                  {opp.list_summary?.generated_by_name ?? "—"}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={
                      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium " +
                      (STATUS_BADGE[opp.status] ?? STATUS_BADGE.new)
                    }
                  >
                    {OPPORTUNITY_STATUS_LABELS[opp.status] ?? opp.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-500 dark:text-zinc-400">
                  {formatTimestamp(opp.updated_at)}
                </td>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1">
                    {!opp.archived_at ? (
                      <button
                        type="button"
                        disabled={busyId === opp.id}
                        onClick={(e) => void handleArchive(e, opp.id)}
                        className="px-2 py-0.5 rounded border border-gray-300 text-[10px] text-gray-600 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-300"
                      >
                        Archive
                      </button>
                    ) : null}
                    {canDeleteOpportunity(opp) ? (
                      <button
                        type="button"
                        disabled={busyId === opp.id}
                        onClick={(e) => void handleDelete(e, opp.id)}
                        className="px-2 py-0.5 rounded border border-red-300 text-[10px] text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300"
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {opportunities.length > table.pageSize ? (
        <ListControls
          total={opportunities.length}
          rangeStart={table.rangeStart}
          rangeEnd={table.rangeEnd}
          page={table.page}
          pageCount={table.pageCount}
          pageSize={table.pageSize}
          sortKey={table.sortKey}
          sortDir={table.sortDir}
          onSetPage={table.setPage}
          onSetPageSize={table.setPageSize}
          onSetSortKey={table.setSortKey}
          onToggleSortDir={table.toggleSortDir}
          canPrev={table.page > 0}
          canNext={table.page + 1 < table.pageCount}
          variant="bottom"
        />
      ) : null}
    </div>
  );
}

function PricingCell(props: { summary: OpportunityListItem["list_summary"] }) {
  const { summary } = props;
  if (
    summary?.total_sale_price == null ||
    summary?.deposit_amount == null ||
    !Number.isFinite(summary.total_sale_price) ||
    !Number.isFinite(summary.deposit_amount)
  ) {
    return <span className="text-gray-400 dark:text-zinc-500">—</span>;
  }
  return (
    <span>
      {formatOpportunityCurrency(summary.total_sale_price)}
      <span className="mx-1 text-gray-400 dark:text-zinc-500">·</span>
      {formatOpportunityCurrency(summary.deposit_amount)} dep.
    </span>
  );
}

function ListControls(props: {
  total: number;
  rangeStart: number;
  rangeEnd: number;
  page: number;
  pageCount: number;
  pageSize: OpportunityListPageSize;
  sortKey: OpportunityListSortKey;
  sortDir: OpportunityListSortDir;
  onSetPage: (page: number) => void;
  onSetPageSize: (size: OpportunityListPageSize) => void;
  onSetSortKey: (key: OpportunityListSortKey) => void;
  onToggleSortDir: () => void;
  canPrev: boolean;
  canNext: boolean;
  variant?: "top" | "bottom";
}) {
  const wrapClass =
    props.variant === "bottom"
      ? "flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-2 dark:border-zinc-700"
      : "flex flex-wrap items-center justify-between gap-2";
  return (
    <div className={wrapClass}>
      <p className="text-[11px] text-gray-600 dark:text-zinc-400">
        Showing{" "}
        <span className="tabular-nums font-medium text-gray-800 dark:text-zinc-200">
          {props.rangeStart}
        </span>
        –
        <span className="tabular-nums font-medium text-gray-800 dark:text-zinc-200">
          {props.rangeEnd}
        </span>{" "}
        of{" "}
        <span className="tabular-nums font-medium text-gray-800 dark:text-zinc-200">{props.total}</span>
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-zinc-400">
          Sort
          <select
            className="rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
            value={props.sortKey}
            onChange={(e) => props.onSetSortKey(e.target.value as OpportunityListSortKey)}
          >
            <option value="updated">Updated date</option>
            <option value="user">Assignee</option>
            <option value="location">Location</option>
          </select>
        </label>
        <button
          type="button"
          onClick={props.onToggleSortDir}
          className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-800 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
          title="Toggle sort direction"
        >
          {props.sortDir === "asc" ? "Asc ↑" : "Desc ↓"}
        </button>
        <label className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-zinc-400">
          Per page
          <select
            className="rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
            value={props.pageSize}
            onChange={(e) => props.onSetPageSize(Number(e.target.value) as OpportunityListPageSize)}
          >
            {OPPORTUNITY_LIST_PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={!props.canPrev}
            onClick={() => props.onSetPage(props.page - 1)}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-800 disabled:opacity-40 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
          >
            Prev
          </button>
          <span className="px-1 text-[11px] tabular-nums text-gray-600 dark:text-zinc-400">
            {props.page + 1} / {props.pageCount}
          </span>
          <button
            type="button"
            disabled={!props.canNext}
            onClick={() => props.onSetPage(props.page + 1)}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-800 disabled:opacity-40 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso ?? "";
  return d.toLocaleString();
}
